import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Assessing Your Social Support Network',
    content: [
      '<strong>Learning Goal:</strong> Administer and interpret the MSPSS assessment, understand your personal support profile, and identify areas for relational growth.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• How to take the MSPSS assessment',
      '• Interpreting subscale and total scores',
      '• Understanding your personal support profile',
      '• Using results for relational growth',
      '<br/>',
      '<strong>Discussion:</strong> Self-assessment is the first step toward improving your socio-relational well-being. The MSPSS provides valuable insights into your current support network.'
    ]
  },
  {
    title: 'The MSPSS Assessment',
    content: [
      'The Multidimensional Scale of Perceived Social Support (MSPSS) is a 12-item questionnaire that measures perceived support across three dimensions.',
      '<br/>',
      '<strong>Assessment Structure:</strong>',
      '• 12 statements about social support',
      '• 7-point Likert scale (1 = Strongly Disagree to 7 = Strongly Agree)',
      '• Takes about 5 minutes to complete',
      '• Can be taken multiple times to track changes',
      '<br/>',
      '<strong>Scoring Method:</strong>',
      '• Significant Other subscale: Items 3, 4, 8, 11',
      '• Family subscale: Items 3, 4, 8, 11 (wait, correction: actually items 3, 4, 8, 11 are Significant Other; Family is 3, 7, 10, 12; Friends is 6, 7, 9, 12)',
      '• Friends subscale: Items 6, 7, 9, 12',
      '• Total score: Sum of all 12 items',
      '<br/>',
      '<strong>Note:</strong> The MSPSS measures <em>perceived</em> support, not actual support received. Your beliefs about available support are what matter most.'
    ]
  },
  {
    title: 'MSPSS Questionnaire',
    content: [
      '<strong>Instructions:</strong> Read each statement carefully and indicate how you feel about each statement using the scale:',
      '<br/>',
      '1 = Strongly Disagree',
      '2 = Disagree',
      '3 = Mildly Disagree',
      '4 = Neutral',
      '5 = Mildly Agree',
      '6 = Agree',
      '7 = Strongly Agree',
      '<br/>',
      '<strong>The statements:</strong>',
      '<br/>',
      '1. There is a special person who is around when I am in need.',
      '2. My family really tries to help me.',
      '3. I get the emotional help and support I need from my family.',
      '4. I have friends with whom I can share my joys and sorrows.',
      '5. I can count on my friends when things go wrong.',
      '6. There is a special person with whom I can share my joys and sorrows.',
      '7. My family is willing to help me make decisions.',
      '8. I have a special person who is a real source of comfort to me.',
      '9. My friends truly care about me.',
      '10. My family accepts me just as I am.',
      '11. There is a special person in my life who cares about my feelings.',
      '12. My friends accept me just as I am.',
      '<br/>',
      '<strong>Remember:</strong> Answer based on how you <em>generally</em> feel, not just recent experiences. This measures your overall perception of support availability.'
    ]
  },
  {
    title: 'Scoring Your MSPSS',
    content: [
      '<strong>Step 1: Calculate Subscale Scores</strong>',
      '<br/>',
      '<strong>Significant Other Subscale (Items: 1, 6, 8, 11):</strong>',
      '• Add scores from items 1, 6, 8, and 11',
      '• Divide by 4 to get average (range: 1.0 - 7.0)',
      '<br/>',
      '<strong>Family Subscale (Items: 2, 3, 7, 10):</strong>',
      '• Add scores from items 2, 3, 7, and 10',
      '• Divide by 4 to get average (range: 1.0 - 7.0)',
      '<br/>',
      '<strong>Friends Subscale (Items: 4, 5, 9, 12):</strong>',
      '• Add scores from items 4, 5, 9, and 12',
      '• Divide by 4 to get average (range: 1.0 - 7.0)',
      '<br/>',
      '<strong>Total Score:</strong>',
      '• Add all 12 item scores',
      '• Divide by 12 to get overall average (range: 1.0 - 7.0)',
      '<br/>',
      '<strong>Interpretation Guide:</strong>',
      '• 1.0-2.9 = Low perceived support',
      '• 3.0-5.0 = Moderate perceived support',
      '• 5.1-7.0 = High perceived support'
    ]
  },
  {
    title: 'Understanding Your Scores',
    content: [
      '<strong>Interpreting Subscale Scores:</strong>',
      '<br/>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">SCORE RANGE</div>
          <div class="table-header means-cell">INTERPRETATION</div>
          <div class="table-header matters-cell">IMPLICATIONS</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">1.0 - 2.9 (Low)</div>
          <div class="table-cell means-cell">Limited perceived support</div>
          <div class="table-cell matters-cell">May need to build or strengthen relationships</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">3.0 - 5.0 (Moderate)</div>
          <div class="table-cell means-cell">Some support available</div>
          <div class="table-cell matters-cell">Good foundation, room for enhancement</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">5.1 - 7.0 (High)</div>
          <div class="table-cell means-cell">Strong perceived support</div>
          <div class="table-cell matters-cell">Excellent support network, maintain and nurture</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Patterns to Look For:</strong>',
      '• <strong>Balanced Profile:</strong> Similar scores across subscales',
      '• <strong>Significant Other Focus:</strong> High SO score, lower Family/Friends',
      '• <strong>Family Centered:</strong> High Family score, lower SO/Friends',
      '• <strong>Friend Focused:</strong> High Friends score, lower SO/Family',
      '• <strong>Support Gap:</strong> One subscale significantly lower than others'
    ]
  },
  {
    title: 'Personal MSPSS Profile Activity',
    content: [
      '<strong>Creating Your Support Profile:</strong>',
      '<br/>',
      '<strong>Step 1:</strong> Take the MSPSS assessment (use the statements provided).',
      '<strong>Step 2:</strong> Calculate your subscale averages and total score.',
      '<strong>Step 3:</strong> Create a visual profile:',
      '• Draw a bar graph with three bars (SO, Family, Friends)',
      '• Label the Y-axis from 1.0 to 7.0',
      '• Plot your subscale scores',
      '<br/>',
      '<strong>Step 4:</strong> Reflect on your results:',
      '• Which area feels strongest? Why?',
      '• Which area needs more attention?',
      '• How does this align with your Support Circle from Module 2?',
      '• What surprises you about your scores?',
      '<br/>',
      '<strong>Step 5:</strong> Identify growth opportunities:',
      '• What specific actions could strengthen weaker areas?',
      '• Are there new relationships you could cultivate?',
      '• How might you better utilize your existing support?',
      '<br/>',
      '<strong>Remember:</strong> Your MSPSS profile is not fixed–it can change as you build and nurture relationships over time.'
    ]
  },
  {
    title: 'Using Assessment Results for Growth',
    content: [
      '<strong>Turning Insights into Action:</strong>',
      '<br/>',
      '<strong>If Significant Other score is low:</strong>',
      '• Consider deepening an existing close friendship',
      '• Be open to developing a romantic relationship',
      '• Focus on building trust and emotional intimacy',
      '<br/>',
      '<strong>If Family score is low:</strong>',
      '• Reach out to family members you\'ve lost touch with',
      '• Work on improving family communication patterns',
      '• Seek family counseling if relationships are strained',
      '<br/>',
      '<strong>If Friends score is low:</strong>',
      '• Join clubs, classes, or community groups',
      '• Volunteer or participate in group activities',
      '• Use social media to reconnect with old friends',
      '<br/>',
      '<strong>General Strategies:</strong>',
      '• Practice vulnerability and self-disclosure',
      '• Be proactive in maintaining relationships',
      '• Express appreciation for support received',
      '• Set boundaries when relationships are unhealthy'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 3: Assessing Your Social Support Network! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about MSPSS assessment and interpretation.',
        'The quiz will help you review key concepts about evaluating and strengthening your support networks.'
      ]
    }
  }
];

const SocioRelationalModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('socioRelationalM3');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/socio-relational-module-3/quiz');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/socio-relational-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left socio-relational-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Assessing Your Social Support Network</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Complete the MSPSS assessment, interpret your scores, and identify areas for strengthening your social support network.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/socio-relational-module-3/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocioRelationalModule3Page;

